// pricing-tax-brackets — client-side helpers for the tax drawer's
// "Preisstaffel" table (TaxModelPriceBracket, Hamburg/Hannover style):
// value tidying, the inline guards that hold the autosave, the lower-bound
// labels, and a mirror of the engine's per-person lookup for the drawer's
// "Testen" probe. Pure functions over the Alpine tax row (`tx`), registered
// as the `$taxBrackets` magic in alpine-core-registrations.ts.
//
// The guards are a client gate only — the server owns the real checks
// (pricingTaxRowForm.priceBrackets for parsing, validatePriceBrackets in
// internal/pricing/price_bracket.go for shape). They exist so a half-typed
// table holds the autosave with a calm inline hint instead of round-tripping
// into an error.
//
// Guards judge the RAW cell value exactly as the server will parse it
// (`postable`): the hidden form mirrors post what is in the cell, and the
// autosave fires on state changes (debounced), possibly before the @blur
// that tidies a pasted "10 €". So a pasted "10 €" reads invalid until blur
// tidies it to "10". euroCents (tidied) is for display and the probe.

export interface BracketRow {
  up_to_euros?: string;
  amount_euros?: string;
}

export interface BracketTax {
  model?: string;
  percent_base?: string;
  rest_json?: string;
  brackets?: BracketRow[];
  bracket_top?: string;
  bracket_top_amount_euros?: string;
  escalation_every_euros?: string;
  escalation_add_euros?: string;
  escalation_max_euros?: string;
}

const EURO_RE = /^\d+([.,]\d{1,2})?$/;

function str(v: unknown): string {
  return String(v ?? "");
}

function rows(tx: BracketTax): BracketRow[] {
  return Array.isArray(tx?.brackets) ? tx.brackets : [];
}

function escalates(tx: BracketTax): boolean {
  return tx?.bracket_top === "escalate";
}

function empty(v: unknown): boolean {
  return str(v).trim() === "";
}

// parse converts an already-normalized string to cents, NaN unless it is a
// plain non-negative amount with at most two decimals.
function parse(s: string): number {
  if (!EURO_RE.test(s)) return NaN;
  return Math.round(Number(s.replace(",", ".")) * 100);
}

// postable is what the server's parseEuroCentsPricing accepts for a
// non-blank value (trimmed, no tidying): cents, or NaN.
function postable(v: unknown): number {
  return parse(str(v).trim());
}

// tidyEuro cleans a value pasted from a statute: strips "€", NBSP and all
// whitespace; when both "." and "," are present the dots are thousands
// separators and are removed. Never touches digits.
export function tidyEuro(v: unknown): string {
  let s = str(v).replace(/€/g, "").replace(/\s/g, "");
  if (s.includes(".") && s.includes(",")) s = s.replace(/\./g, "");
  return s;
}

// euroCents parses a (tidied) euro amount: "25" → 2500, "0,6" → 60,
// "1000.5" → 100050. NaN when blank, unparseable or > 2 decimals.
export function euroCents(v: unknown): number {
  return parse(tidyEuro(v));
}

// formatEuro renders cents German-style without a symbol: 100000 → "1.000,00".
export function formatEuro(cents: number): string {
  if (!Number.isFinite(cents)) return "";
  const abs = Math.abs(Math.round(cents));
  const euros = String(Math.floor(abs / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  const frac = String(abs % 100).padStart(2, "0");
  return `${cents < 0 && abs !== 0 ? "-" : ""}${euros},${frac}`;
}

export function rowBlank(tx: BracketTax, i: number): boolean {
  const r = rows(tx)[i];
  return !!r && (empty(r.up_to_euros) || empty(r.amount_euros));
}

export function rowInvalid(tx: BracketTax, i: number): boolean {
  const r = rows(tx)[i];
  if (!r) return false;
  const bad = (v: unknown) => !empty(v) && Number.isNaN(postable(v));
  return bad(r.up_to_euros) || bad(r.amount_euros);
}

// rowOrder: the first upper limit must be > 0; every later one must exceed
// the previous one (both parseable — a blank/invalid cell has its own hint).
export function rowOrder(tx: BracketTax, i: number): boolean {
  const rs = rows(tx);
  if (!rs[i]) return false;
  const cur = postable(rs[i].up_to_euros);
  if (Number.isNaN(cur)) return false;
  if (i === 0) return cur <= 0;
  const prev = postable(rs[i - 1]?.up_to_euros);
  return !Number.isNaN(prev) && cur <= prev;
}

// topBlank: a flat top needs an amount (0 is legal); an escalation needs a
// positive step size and a positive increase.
export function topBlank(tx: BracketTax): boolean {
  if (!escalates(tx)) return Number.isNaN(postable(tx?.bracket_top_amount_euros));
  const every = postable(tx.escalation_every_euros);
  const add = postable(tx.escalation_add_euros);
  return !(every > 0) || !(add > 0);
}

// topMaxInvalid: an escalation's optional maximum is set but not a number.
export function topMaxInvalid(tx: BracketTax): boolean {
  return escalates(tx) && !empty(tx.escalation_max_euros) && Number.isNaN(postable(tx.escalation_max_euros));
}

// topMaxLow: an escalation's maximum lies below the last bounded amount.
export function topMaxLow(tx: BracketTax): boolean {
  if (!escalates(tx) || empty(tx.escalation_max_euros)) return false;
  const rs = rows(tx);
  if (rs.length === 0) return false;
  const max = postable(tx.escalation_max_euros);
  const last = postable(rs[rs.length - 1].amount_euros);
  return !Number.isNaN(max) && !Number.isNaN(last) && max < last;
}

export function escalateNoRows(tx: BracketTax): boolean {
  return escalates(tx) && rows(tx).length === 0;
}

// issue is the autosave gate: true for a price_bracket row whose table or
// top would not survive the server. Non-bracket models never block.
export function issue(tx: BracketTax): boolean {
  if (tx?.model !== "price_bracket") return false;
  const rs = rows(tx);
  for (let i = 0; i < rs.length; i++) {
    if (rowBlank(tx, i) || rowInvalid(tx, i) || rowOrder(tx, i)) return true;
  }
  return topBlank(tx) || topMaxInvalid(tx) || topMaxLow(tx) || escalateNoRows(tx) || allZero(tx);
}

// allZero mirrors validatePriceBrackets' "can only ever charge 0" rule: every
// postable amount is 0 and the top is not an escalation (whose Add > 0 is
// already required).
function allZero(tx: BracketTax): boolean {
  if (escalates(tx)) return false;
  const amounts = rows(tx).map((r) => postable(r.amount_euros));
  amounts.push(postable(tx.bracket_top_amount_euros));
  return amounts.every((a) => a === 0);
}

// fromLabel is row i's implicit lower bound: "ab 0,00 €" for the first row,
// "über X €" (X = previous upper limit) otherwise, or pending while that
// limit is not a number yet. i === brackets.length is the flat top row.
export function fromLabel(tx: BracketTax, i: number): { kind: "first" | "over" | "pending"; amount: string } {
  if (i <= 0) return { kind: "first", amount: "0,00" };
  const c = euroCents(rows(tx)[i - 1]?.up_to_euros);
  return Number.isNaN(c) ? { kind: "pending", amount: "" } : { kind: "over", amount: formatEuro(c) };
}

// bracketAmount is the client mirror of Go's bracketAmount
// (internal/pricing/price_bracket.go) — same rule, same test vectors
// (pricing-tax-brackets.test.ts ↔ price_bracket_test.go): the first step
// whose upper limit (inclusive) is ≥ share; above the last bounded step
// either the flat top amount, or lastAmount + ceil((share − lastUpTo) /
// every) × add, capped at max. null while the table has an issue.
export function bracketAmount(
  tx: BracketTax,
  shareCents: number,
): { cents: number; step: number; escalations: number; capped: boolean } | null {
  if (issue(tx) || !Number.isFinite(shareCents)) return null;
  const rs = rows(tx);
  for (let i = 0; i < rs.length; i++) {
    if (shareCents <= postable(rs[i].up_to_euros)) {
      return { cents: postable(rs[i].amount_euros), step: i, escalations: 0, capped: false };
    }
  }
  if (!escalates(tx)) {
    return { cents: postable(tx.bracket_top_amount_euros), step: rs.length, escalations: 0, capped: false };
  }
  const last = rs[rs.length - 1];
  const every = postable(tx.escalation_every_euros);
  const k = Math.ceil((shareCents - postable(last.up_to_euros)) / every);
  let cents = postable(last.amount_euros) + k * postable(tx.escalation_add_euros);
  let capped = false;
  if (!empty(tx.escalation_max_euros)) {
    const max = postable(tx.escalation_max_euros);
    if (cents > max) {
      cents = max;
      capped = true;
    }
  }
  return { cents, step: rs.length, escalations: k, capped };
}

// seed prepares a row switched to price_bracket: net base if unset, one
// blank step if there are none, a flat top if unset.
export function seed(tx: BracketTax): void {
  if (empty(tx.percent_base)) tx.percent_base = "net";
  if (rows(tx).length === 0) tx.brackets = [{ up_to_euros: "", amount_euros: "" }];
  if (empty(tx.bracket_top)) tx.bracket_top = "flat";
}

// addRow appends a blank step and returns its index for focusing — unless
// the last step is still incomplete, in which case it returns that one and
// adds nothing (no stacks of blank rows).
export function addRow(tx: BracketTax): number {
  if (!Array.isArray(tx.brackets)) tx.brackets = [];
  const last = tx.brackets.length - 1;
  if (last >= 0 && rowBlank(tx, last)) return last;
  tx.brackets.push({ up_to_euros: "", amount_euros: "" });
  return tx.brackets.length - 1;
}
